package ui.widget

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEach
import compose.assets.Svg
import compose.assets.svg.Chevron_down
import org.jetbrains.compose.resources.StringResource
import tools.rememberString

interface DropDownResItem {
    val label: StringResource
    val image: ImageVector?
}

@Composable
fun <T : DropDownResItem> DropDownPickerCard(
    selected: MutableState<T>,
    values: List<T>,
    itemWidth: Dp,
    modifier: Modifier = Modifier,
    previewImage: ImageVector? = null,
    previewLabel: String? = null,
    enabled: Boolean = true,
    useItemWidthForCardWidth: Boolean = true,
    colors: CardColors = CardDefaults.cardColors(),
    onChange: (T) -> Unit = {}
) {
    DropDownResPicker(
        values = values,
        itemWidth = itemWidth,
        modifier = modifier,
        onChange = {
            selected.value = it
            onChange(it)
        }
    ) { expanded: MutableState<Boolean> ->
        val animation by animateFloatAsState(
            animationSpec = tween(200),
            targetValue = if (expanded.value) 180F
            else 0F
        )
        Card(
            onClick = { expanded.value = !expanded.value },
            enabled = enabled,
            colors = colors,
            modifier = Modifier
                .let {
                    if (useItemWidthForCardWidth) it.width(itemWidth)
                    else it
                }
                .clip(CardDefaults.shape)
        ) {
            Row(Modifier.padding(12.dp)) {

                previewLabel?.also {
                    Text(
                        text = it,
                        modifier = Modifier.align(Alignment.CenterVertically)
                    )
                    Space(16.dp)
                }

                (selected.value.image ?: previewImage)?.also {
                    Icon(
                        imageVector = it,
                        contentDescription = null,
                        modifier = Modifier.align(Alignment.CenterVertically)
                    )
                    Space(16.dp)
                }

                Text(
                    text = selected.value.label.rememberString(),
                    modifier = Modifier.weight(1F)
                        .align(Alignment.CenterVertically)
                )
                Icon(
                    imageVector = Svg.Chevron_down,
                    contentDescription = null,
                    modifier = Modifier
                        .align(Alignment.CenterVertically)
                        .rotate(animation)
                )
            }
        }
    }
}

@Composable
fun <T : DropDownResItem> DropDownPickerCard(
    selected: () -> T,
    values: List<T>,
    itemWidth: Dp,
    modifier: Modifier = Modifier,
    previewImage: ImageVector? = null,
    previewLabel: String? = null,
    enabled: Boolean = true,
    useItemWidthForCardWidth: Boolean = true,
    colors: CardColors = CardDefaults.cardColors(),
    shape: Shape = CardDefaults.shape,
    onChange: (T) -> Unit = {}
) {
    var selection by remember {
        mutableStateOf(selected())
    }
    DropDownResPicker(
        values = values,
        itemWidth = itemWidth,
        modifier = modifier,
        onChange = {
            selection = it
            onChange(it)
        }
    ) { expanded: MutableState<Boolean> ->
        val animation by animateFloatAsState(
            animationSpec = tween(200),
            targetValue = if (expanded.value) 180F
            else 0F
        )
        Card(
            onClick = { expanded.value = !expanded.value },
            enabled = enabled,
            colors = colors,
            shape = shape,
            modifier = Modifier
                .let {
                    if (useItemWidthForCardWidth) it.width(itemWidth)
                    else it
                }
                .clip(shape)
        ) {
            Row(Modifier.padding(12.dp)) {
                previewLabel?.also {
                    Text(
                        text = it,
                        modifier = Modifier.align(Alignment.CenterVertically)
                    )
                    Space(16.dp)
                }

                with(selection) {
                    (image ?: previewImage)?.also {
                        Icon(
                            imageVector = it,
                            contentDescription = null,
                            modifier = Modifier.align(Alignment.CenterVertically)
                        )
                        Space(16.dp)
                    }

                    Text(
                        text = label.rememberString(),
                        modifier = Modifier.weight(1F)
                            .align(Alignment.CenterVertically)
                    )
                    Icon(
                        imageVector = Svg.Chevron_down,
                        contentDescription = null,
                        modifier = Modifier
                            .align(Alignment.CenterVertically)
                            .rotate(animation)
                    )
                }

            }
        }
    }
}

@Composable
fun <T : DropDownResItem> DropDownPickerButton(
    selected: MutableState<T>,
    values: List<T>,
    itemWidth: Dp,
    previewImage: ImageVector? = null,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    colors: CardColors = CardDefaults.cardColors(
        containerColor = MaterialTheme.colorScheme.primaryContainer
    ),
    shape: Shape = CardDefaults.shape,
    iconPadding: Dp = 12.dp,
    onChange: (T) -> Unit = {}
) {
    DropDownResPicker(
        values = values,
        itemWidth = itemWidth,
        modifier = modifier,
        onChange = {
            selected.value = it
            onChange(it)
        }
    ) { expanded: MutableState<Boolean> ->
        Card(
            onClick = { expanded.value = !expanded.value },
            enabled = enabled,
            shape = shape,
            colors = colors
        ) {
            (selected.value.image ?: previewImage)?.also {
                Icon(
                    imageVector = it,
                    contentDescription = null,
                    modifier = Modifier.padding(iconPadding)
                )
            }
        }
    }
}

@Composable
fun <T : DropDownResItem> DropDownResPicker(
    values: List<T>,
    itemWidth: Dp,
    modifier: Modifier = Modifier,
    onChange: (T) -> Unit = {},
    content: @Composable (expanded: MutableState<Boolean>) -> Unit,
) {
    val isExpanded = remember {
        mutableStateOf(false)
    }

    Box(modifier) {
        content(isExpanded)
        DropdownMenu(
            expanded = isExpanded.value,
            modifier = Modifier.clip(CardDefaults.shape),
            onDismissRequest = { isExpanded.value = false }) {
            values.fastForEach { value ->
                DropdownMenuItem(
                    modifier = Modifier.let {
                        if (itemWidth == Dp.Unspecified) it
                        else it.width(itemWidth)
                    },
                    text = {
                        Row {
                            value.image?.also {
                                Icon(it, null)
                                Space(16.dp)
                            }
                            Text(value.label.rememberString(), Modifier.align(Alignment.CenterVertically))
                        }
                    },
                    onClick = {
                        onChange(value)
                        isExpanded.value = false
                    })
            }
        }
    }
}